// Entradas por nota fiscal (regras 1–5): cabeçalho da NF + itens com conversão para a unidade
// padrão, conferência do total em tempo real (tolerância do parâmetro nf_tolerancia_valor) e
// anexo PDF/XML. Regras no backend em services/estoque/notas_fiscais.py. A entrada pelo XML da
// NF-e (itens lidos do arquivo + vínculo com os produtos) fica em ImportacaoXmlNF.js.
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, FileCode2, FileText, Paperclip, Plus, Trash2, Upload, X } from "lucide-react";
import {
  baixarAnexoNotaFiscal, getEstoqueCategorias, getEstoqueFornecedores, getEstoqueNotaFiscal, getEstoqueNotasFiscais, getEstoqueProdutos,
  getEstoqueSugestaoFator, getEstoqueUnidades, getRoleFromToken, postEstoqueAnexoNotaFiscal,
  postEstoqueCancelarNotaFiscal, postEstoqueFornecedor, postEstoqueNotaFiscal,
} from "../../api";
import { permissoesEstoque } from "../../constants/estoque";
import EstoqueLayout from "./EstoqueLayout";
import ImportacaoXmlNF from "./ImportacaoXmlNF";
import { AvisoSemAbertura, ModalCancelar, ProdutoSelect, formatData, hojeISO, mensagemErro, useContextoEstoque } from "./comum";
import { AvisoErro, Campo, Modal, NumeroInput, btnStyle, formatBRL, formatQtd, inputStyle, palette, parseNumero, useIsMobile } from "./ui";

const num = (texto) => {
  const p = parseNumero(texto);
  return p === null ? null : Number(p);
};
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const mascaraCnpj = (valor) => valor.replace(/\D/g, "").slice(0, 14)
  .replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
  .replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");
const formatCnpj = (c) => (c && c.length === 14 ? mascaraCnpj(c) : c);

let _seq = 0;
const novoItem = () => ({ chave: ++_seq, produto_id: null, quantidade_nf: "", unidade_nf_id: "", fator: "1", valor_unitario: "", valor_total: "", totalManual: false, sugestao: null });

// ─── Fornecedor rápido ────────────────────────────────────────────────────────

function ModalFornecedorRapido({ C, onClose, onCriado }) {
  const [form, setForm] = useState({ razao_social: "", cnpj: "", contato: "" });
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      onCriado(await postEstoqueFornecedor({ razao_social: form.razao_social.trim(), cnpj: form.cnpj, contato: form.contato.trim() || null }));
    } catch (e) {
      setErro(mensagemErro(e));
      setSalvando(false);
    }
  }
  return (
    <Modal C={C} titulo="Novo fornecedor" onClose={onClose} largura={440}
      rodape={<>
        <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
        <button onClick={salvar} disabled={salvando || !form.razao_social.trim() || !form.cnpj} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>Salvar</button>
      </>}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Campo label="Razão social" C={C} obrigatorio><input value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} style={inputStyle(C)} autoFocus /></Campo>
        <Campo label="CNPJ" C={C} obrigatorio><input value={form.cnpj} inputMode="numeric" onChange={(e) => setForm({ ...form, cnpj: mascaraCnpj(e.target.value) })} style={inputStyle(C)} /></Campo>
        <Campo label="Contato" C={C}><input value={form.contato} onChange={(e) => setForm({ ...form, contato: e.target.value })} style={inputStyle(C)} /></Campo>
      </div>
    </Modal>
  );
}

// ─── Formulário da NF ─────────────────────────────────────────────────────────

function ItemNF({ C, item, idx, produtos, unidades, podeRemover, onChange, onRemover }) {
  const produto = produtos.find((p) => p.id === item.produto_id);
  const siglaPadrao = produto?.unidade_padrao_sigla;
  const q = num(item.quantidade_nf);
  const f = num(item.fator);
  const total = num(item.valor_total);
  const convertida = q && f ? q * f : null;
  const custoPadrao = convertida && total !== null ? total / convertida : null;
  const unidadeNF = unidades.find((u) => u.id === Number(item.unidade_nf_id));
  const mesmaUnidade = produto && Number(item.unidade_nf_id) === produto.unidade_padrao_id;

  function set(campo, valor) {
    const novo = { ...item, [campo]: valor };
    if (campo === "valor_total") novo.totalManual = valor !== "";
    if ((campo === "quantidade_nf" || campo === "valor_unitario") && !novo.totalManual) {
      const qq = num(novo.quantidade_nf);
      const uu = num(novo.valor_unitario);
      novo.valor_total = qq !== null && uu !== null ? round2(qq * uu).toFixed(2).replace(".", ",") : "";
    }
    if (campo === "unidade_nf_id" && produto && Number(valor) === produto.unidade_padrao_id) novo.fator = "1";
    onChange(novo);
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Campo label={`Item ${idx + 1} · Produto`} C={C} obrigatorio>
            <ProdutoSelect C={C} produtos={produtos} value={item.produto_id} onChange={(id) => onChange({ ...item, produto_id: id, _trocouProduto: true })} />
          </Campo>
        </div>
        {podeRemover && (
          <button onClick={onRemover} aria-label="Remover item" style={{ width: 42, height: 42, borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.danger, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Trash2 size={16} />
          </button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))", gap: 10 }}>
        <Campo label="Qtd. na NF" C={C} obrigatorio><NumeroInput C={C} value={item.quantidade_nf} onChange={(v) => set("quantidade_nf", v)} /></Campo>
        <Campo label="Unidade da NF" C={C} obrigatorio>
          <select value={item.unidade_nf_id} onChange={(e) => set("unidade_nf_id", e.target.value)} style={inputStyle(C)}>
            <option value="">—</option>
            {unidades.map((u) => <option key={u.id} value={u.id}>{u.sigla}</option>)}
          </select>
        </Campo>
        <Campo label={siglaPadrao ? `Fator (${siglaPadrao} por ${unidadeNF?.sigla || "emb."})` : "Fator"} C={C} obrigatorio>
          <NumeroInput C={C} casas={6} value={item.fator} onChange={(v) => set("fator", v)} disabled={mesmaUnidade} />
        </Campo>
        <Campo label="Valor unit. NF (R$)" C={C}><NumeroInput C={C} casas={6} value={item.valor_unitario} onChange={(v) => set("valor_unitario", v)} /></Campo>
        <Campo label="Valor total item (R$)" C={C} obrigatorio><NumeroInput C={C} casas={2} value={item.valor_total} onChange={(v) => set("valor_total", v)} /></Campo>
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: C.muted, display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
        {convertida !== null && siglaPadrao && (
          <span style={{ color: C.text, fontWeight: 600 }}>
            = {formatQtd(convertida)} {siglaPadrao}{custoPadrao !== null ? ` · ${formatBRL(custoPadrao)}/${siglaPadrao}` : ""}
          </span>
        )}
        {item.sugestao && (
          <span>Sugerido pela NF {item.sugestao.nf_numero}{item.sugestao.mesmo_fornecedor ? " deste fornecedor" : " (outro fornecedor)"}: {item.sugestao.unidade_nf_sigla} × {formatQtd(item.sugestao.fator_conversao)}</span>
        )}
      </div>
    </div>
  );
}

function FormNotaFiscal({ C, contexto, fornecedores, setFornecedores, produtos, unidades, onCancelar, onSalva }) {
  const isMobile = useIsMobile();
  const [cab, setCab] = useState({ fornecedor_id: "", numero: "", serie: "", data_emissao: hojeISO(), data_entrada: hojeISO(), valor_total: "", chave_acesso: "", observacao: "" });
  const [itens, setItens] = useState([novoItem()]);
  const [justificativa, setJustificativa] = useState("");
  const [anexo, setAnexo] = useState(null);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [modalFornecedor, setModalFornecedor] = useState(false);
  const tolerancia = Number(contexto.nf_tolerancia_valor);

  const produtosAtivos = useMemo(() => produtos.filter((p) => p.ativo), [produtos]);
  const soma = itens.reduce((s, i) => s + (num(i.valor_total) || 0), 0);
  const totalNF = num(cab.valor_total);
  const diferenca = totalNF !== null ? round2(totalNF - soma) : null;
  const divergente = diferenca !== null && Math.abs(diferenca) > tolerancia + 1e-9;

  async function atualizarItem(chave, novo) {
    if (novo._trocouProduto) {
      delete novo._trocouProduto;
      const produto = produtosAtivos.find((p) => p.id === novo.produto_id);
      novo.unidade_nf_id = produto ? String(produto.unidade_padrao_id) : "";
      novo.fator = "1";
      novo.sugestao = null;
      setItens((lista) => lista.map((i) => (i.chave === chave ? novo : i)));
      if (produto) {
        try {
          const { sugestao } = await getEstoqueSugestaoFator(produto.id, cab.fornecedor_id || null);
          if (sugestao) {
            setItens((lista) => lista.map((i) => (i.chave === chave && i.produto_id === produto.id
              ? { ...i, sugestao, unidade_nf_id: String(sugestao.unidade_nf_id), fator: String(sugestao.fator_conversao).replace(".", ",") }
              : i)));
          }
        } catch { /* sugestão é só conveniência */ }
      }
      return;
    }
    setItens((lista) => lista.map((i) => (i.chave === chave ? novo : i)));
  }

  function validar() {
    if (!cab.fornecedor_id || !cab.numero.trim() || !cab.data_emissao || !cab.data_entrada || totalNF === null) return "Preencha fornecedor, número, datas e valor total da NF.";
    for (const [idx, i] of itens.entries()) {
      if (!i.produto_id || !num(i.quantidade_nf) || !i.unidade_nf_id || !num(i.fator) || num(i.valor_total) === null) {
        return `Item ${idx + 1}: preencha produto, quantidade (> 0), unidade, fator e valor total.`;
      }
    }
    if (divergente && !justificativa.trim()) return "A soma dos itens não bate com o total da NF — informe a justificativa (frete, desconto, imposto…).";
    return null;
  }

  async function salvar() {
    const problema = validar();
    if (problema) { setErro(problema); return; }
    setErro("");
    setSalvando(true);
    const dados = {
      numero: cab.numero.trim(),
      serie: cab.serie.trim() || null,
      fornecedor_id: Number(cab.fornecedor_id),
      data_emissao: cab.data_emissao,
      data_entrada: cab.data_entrada,
      valor_total: parseNumero(cab.valor_total),
      chave_acesso: cab.chave_acesso.trim() || null,
      observacao: cab.observacao.trim() || null,
      justificativa_divergencia: divergente ? justificativa.trim() : null,
      itens: itens.map((i) => ({
        produto_id: i.produto_id,
        quantidade_nf: parseNumero(i.quantidade_nf),
        unidade_nf_id: Number(i.unidade_nf_id),
        fator_conversao: parseNumero(i.fator),
        valor_unitario_nf: parseNumero(i.valor_unitario),
        valor_total_item: parseNumero(i.valor_total),
      })),
    };
    try {
      const nf = await postEstoqueNotaFiscal(dados);
      let avisoAnexo = null;
      if (anexo) {
        try {
          await postEstoqueAnexoNotaFiscal(nf.id, anexo);
        } catch (e) {
          avisoAnexo = `A NF foi lançada, mas o anexo falhou: ${e.message}. Anexe de novo no detalhe da nota.`;
        }
      }
      onSalva(nf, avisoAnexo);
    } catch (e) {
      setErro(mensagemErro(e));
      setSalvando(false);
    }
  }

  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 };
  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Nova entrada por NF</h2>
        <button onClick={onCancelar} style={btnStyle(C.muted, true)}><X size={15} /> Cancelar</button>
      </div>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Campo label="Fornecedor" C={C} obrigatorio>
              <select value={cab.fornecedor_id} onChange={(e) => setCab({ ...cab, fornecedor_id: e.target.value })} style={inputStyle(C)}>
                <option value="">Selecione</option>
                {fornecedores.filter((f) => f.ativo).map((f) => <option key={f.id} value={f.id}>{f.razao_social} · {formatCnpj(f.cnpj)}</option>)}
              </select>
            </Campo>
          </div>
          <button onClick={() => setModalFornecedor(true)} style={{ ...btnStyle(C.brand, true), flexShrink: 0 }}><Plus size={15} /> {isMobile ? "" : "Fornecedor"}</button>
        </div>
        <div style={grid}>
          <Campo label="Número da NF" C={C} obrigatorio><input value={cab.numero} inputMode="numeric" onChange={(e) => setCab({ ...cab, numero: e.target.value.replace(/\D/g, "") })} style={inputStyle(C)} /></Campo>
          <Campo label="Série" C={C}><input value={cab.serie} onChange={(e) => setCab({ ...cab, serie: e.target.value })} style={inputStyle(C)} /></Campo>
          <Campo label="Data de emissão" C={C} obrigatorio><input type="date" value={cab.data_emissao} max={cab.data_entrada || hojeISO()} onChange={(e) => setCab({ ...cab, data_emissao: e.target.value })} style={inputStyle(C)} /></Campo>
          <Campo label="Data de entrada" C={C} obrigatorio><input type="date" value={cab.data_entrada} min={contexto.data_abertura || undefined} max={hojeISO()} onChange={(e) => setCab({ ...cab, data_entrada: e.target.value })} style={inputStyle(C)} /></Campo>
          <Campo label="Valor total da NF (R$)" C={C} obrigatorio><NumeroInput C={C} casas={2} value={cab.valor_total} onChange={(v) => setCab({ ...cab, valor_total: v })} /></Campo>
        </div>
        <div style={{ ...grid, marginTop: 12 }}>
          <Campo label="Chave de acesso (opcional)" C={C}><input value={cab.chave_acesso} inputMode="numeric" maxLength={54} onChange={(e) => setCab({ ...cab, chave_acesso: e.target.value.replace(/[^\d ]/g, "") })} placeholder="44 dígitos" style={inputStyle(C)} /></Campo>
          <Campo label="Anexo PDF/XML (opcional)" C={C}>
            <label style={{ ...inputStyle(C), display: "flex", alignItems: "center", gap: 8, cursor: "pointer", overflow: "hidden" }}>
              <Paperclip size={15} color={C.muted} />
              <span style={{ fontSize: 13, color: anexo ? C.text : C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{anexo ? anexo.name : "Escolher arquivo"}</span>
              <input type="file" accept=".pdf,.xml,application/pdf,text/xml,application/xml" onChange={(e) => setAnexo(e.target.files?.[0] || null)} style={{ display: "none" }} />
            </label>
          </Campo>
          <Campo label="Observação" C={C}><input value={cab.observacao} onChange={(e) => setCab({ ...cab, observacao: e.target.value })} style={inputStyle(C)} /></Campo>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {itens.map((item, idx) => (
          <ItemNF key={item.chave} C={C} item={item} idx={idx} produtos={produtosAtivos} unidades={unidades.filter((u) => u.ativo)}
            podeRemover={itens.length > 1}
            onChange={(novo) => atualizarItem(item.chave, novo)}
            onRemover={() => setItens((l) => l.filter((i) => i.chave !== item.chave))} />
        ))}
        <button onClick={() => setItens((l) => [...l, novoItem()])} style={{ ...btnStyle(C.brand, true), alignSelf: "flex-start" }}><Plus size={15} /> Adicionar item</button>
      </div>

      {divergente && (
        <div style={{ marginTop: 16, background: `${C.danger}10`, border: `1px solid ${C.danger}`, borderRadius: 12, padding: 12 }}>
          <Campo label="Justificativa da divergência" C={C} obrigatorio ajuda="Ex.: frete destacado na nota, desconto no total, imposto (ST/IPI) fora dos itens.">
            <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={2} style={{ ...inputStyle(C), resize: "vertical" }} />
          </Campo>
        </div>
      )}

      {/* Conferência do total, sempre visível */}
      <div style={{ position: "sticky", bottom: 0, zIndex: 30, marginTop: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 -6px 24px rgba(0,0,0,0.12)", padding: isMobile ? "10px 12px" : "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <div style={{ display: "flex", gap: isMobile ? 10 : 20, flexWrap: "wrap", fontSize: 13, flex: 1, justifyContent: isMobile ? "flex-start" : "flex-end" }}>
          <span>Itens: <strong>{formatBRL(soma)}</strong></span>
          <span>NF: <strong>{totalNF !== null ? formatBRL(totalNF) : "—"}</strong></span>
          {diferenca !== null && (
            <span style={{ color: divergente ? C.danger : C.success, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
              {divergente ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />} Diferença {formatBRL(diferenca)}
            </span>
          )}
        </div>
        <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.success), opacity: salvando ? 0.6 : 1, minWidth: 150 }}>
          {salvando ? "Lançando..." : "Lançar entrada"}
        </button>
      </div>

      {modalFornecedor && (
        <ModalFornecedorRapido C={C} onClose={() => setModalFornecedor(false)}
          onCriado={(f) => { setFornecedores((l) => [...l, f]); setCab((c) => ({ ...c, fornecedor_id: String(f.id) })); setModalFornecedor(false); }} />
      )}
    </div>
  );
}

// ─── Detalhe ──────────────────────────────────────────────────────────────────

function ModalDetalheNF({ C, nfId, perm, aviso, onClose, onAlterada }) {
  const [nf, setNf] = useState(null);
  const [erro, setErro] = useState(aviso || "");
  const [cancelando, setCancelando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(() => getEstoqueNotaFiscal(nfId).then(setNf).catch((e) => setErro(e.message)), [nfId]);
  useEffect(() => { carregar(); }, [carregar]);

  async function anexar(arquivo) {
    if (!arquivo) return;
    setEnviando(true);
    try {
      setNf(await postEstoqueAnexoNotaFiscal(nfId, arquivo));
      onAlterada();
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal C={C} titulo={nf ? `NF ${nf.numero}${nf.serie ? `/${nf.serie}` : ""}` : "Nota fiscal"} onClose={onClose} largura={760}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {!nf ? <div style={{ color: C.muted, fontSize: 13 }}>Carregando...</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {nf.status === "CANCELADA" && (
            <div style={{ background: `${C.danger}14`, color: C.danger, borderRadius: 8, padding: 10, fontSize: 13 }}>
              Cancelada em {formatData(nf.cancelado_em)}: {nf.motivo_cancelamento}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, fontSize: 13 }}>
            <div><div style={{ color: C.muted, fontSize: 11 }}>Fornecedor</div><strong>{nf.fornecedor_nome}</strong><div style={{ color: C.muted, fontSize: 12 }}>{formatCnpj(nf.fornecedor_cnpj)}</div></div>
            <div><div style={{ color: C.muted, fontSize: 11 }}>Emissão / Entrada</div>{formatData(nf.data_emissao)} · {formatData(nf.data_entrada)}</div>
            <div><div style={{ color: C.muted, fontSize: 11 }}>Valor total</div><strong>{formatBRL(nf.valor_total)}</strong></div>
            <div><div style={{ color: C.muted, fontSize: 11 }}>Lançada por</div>{nf.criado_por_nome || "—"}</div>
          </div>
          {Number(nf.valor_itens_ignorados) > 0 && (
            <div style={{ fontSize: 13, background: C.bgActive, borderRadius: 8, padding: 10 }}>
              {formatBRL(nf.valor_itens_ignorados)} em itens que não são de estoque (fora do saldo) · itens de estoque {formatBRL(nf.soma_itens)}
            </div>
          )}
          {Number(nf.divergencia_valor) !== 0 && (
            <div style={{ fontSize: 13, background: C.bgActive, borderRadius: 8, padding: 10 }}>
              Soma dos itens {formatBRL(nf.soma_itens)} · diferença {formatBRL(nf.divergencia_valor)}
              {nf.justificativa_divergencia && <> — <em>{nf.justificativa_divergencia}</em></>}
            </div>
          )}
          {nf.chave_acesso && <div style={{ fontSize: 12, color: C.muted, wordBreak: "break-all" }}>Chave: {nf.chave_acesso}</div>}
          <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ color: C.muted, textTransform: "uppercase", fontSize: 10 }}>
                  {["Produto", "Qtd. NF", "Fator", "Qtd. padrão", "Valor item", "Custo/un."].map((h, i) => (
                    <th key={h} style={{ padding: 8, textAlign: i === 0 ? "left" : "right", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nf.itens.map((i) => (
                  <tr key={i.id}>
                    <td style={{ padding: 8, borderBottom: `1px solid ${C.border}` }}>{i.produto_nome}</td>
                    <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{formatQtd(i.quantidade_nf)} {i.unidade_nf_sigla}</td>
                    <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>× {formatQtd(i.fator_conversao)}</td>
                    <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{formatQtd(i.quantidade_convertida)} {i.unidade_padrao_sigla}</td>
                    <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>{formatBRL(i.valor_total_item)}</td>
                    <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>{formatBRL(i.custo_unitario_padrao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nf.linhas_xml?.length > 0 && (
            <details style={{ fontSize: 12 }}>
              <summary style={{ cursor: "pointer", color: C.muted, fontWeight: 700 }}>Linhas do XML ({nf.linhas_xml.length})</summary>
              <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <tbody>
                    {nf.linhas_xml.map((l) => (
                      <tr key={l.n_item} style={{ color: l.ignorada ? C.muted : C.text }}>
                        <td style={{ padding: 8, borderBottom: `1px solid ${C.border}` }}>
                          {l.descricao}<div style={{ color: C.muted, fontSize: 11 }}>Cód. {l.codigo_fornecedor}{l.ean ? ` · EAN ${l.ean}` : ""}</div>
                        </td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{formatQtd(l.quantidade)} {l.unidade_xml}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{l.ignorada ? "não é estoque" : `× ${formatQtd(l.fator_conversao)}`}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>{formatBRL(l.valor_item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {nf.tem_anexo && (
              <button onClick={() => baixarAnexoNotaFiscal(nf.id, nf.anexo_nome).catch((e) => setErro(e.message))} style={btnStyle(C.brand, true)}>
                <Download size={15} /> {nf.anexo_nome || "Baixar anexo"}
              </button>
            )}
            {perm.financeiro && (
              <label style={{ ...btnStyle(C.brand, true), opacity: enviando ? 0.6 : 1 }}>
                <Upload size={15} /> {enviando ? "Enviando..." : nf.tem_anexo ? "Substituir anexo" : "Anexar PDF/XML"}
                <input type="file" accept=".pdf,.xml" onChange={(e) => anexar(e.target.files?.[0])} style={{ display: "none" }} />
              </label>
            )}
            {perm.financeiro && nf.status === "ATIVA" && (
              <button onClick={() => setCancelando(true)} style={{ ...btnStyle(C.danger, true), marginLeft: "auto" }}>Cancelar NF</button>
            )}
          </div>
        </div>
      )}
      {cancelando && (
        <ModalCancelar C={C} titulo="Cancelar nota fiscal" onClose={() => setCancelando(false)}
          descricao="As entradas desta NF deixam de contar no estoque (o registro continua no histórico). Só é possível em período aberto."
          onConfirmar={async (dados) => { setNf(await postEstoqueCancelarNotaFiscal(nfId, dados)); setCancelando(false); onAlterada(); }} />
      )}
    </Modal>
  );
}

// ─── View ─────────────────────────────────────────────────────────────────────

export default function NotasFiscaisView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const perm = permissoesEstoque(getRoleFromToken());
  const { contexto, erro: erroContexto } = useContextoEstoque();
  const [modo, setModo] = useState("lista");
  const [nfs, setNfs] = useState([]);
  const [cad, setCad] = useState({ fornecedores: [], produtos: [], unidades: [], categorias: [] });
  const [filtros, setFiltros] = useState({ inicio: "", fim: "", fornecedor_id: "", status: "" });
  const [detalhe, setDetalhe] = useState(null); // { id, aviso }
  const [erro, setErro] = useState("");

  const carregarLista = useCallback(() => getEstoqueNotasFiscais(filtros).then(setNfs).catch((e) => setErro(e.message)), [filtros]);
  useEffect(() => { carregarLista(); }, [carregarLista]);
  useEffect(() => {
    Promise.all([getEstoqueFornecedores(), getEstoqueProdutos(true), getEstoqueUnidades(true), getEstoqueCategorias(true)])
      .then(([fornecedores, produtos, unidades, categorias]) => setCad({ fornecedores, produtos, unidades, categorias }))
      .catch((e) => setErro(e.message));
  }, []);

  const totalLista = nfs.filter((n) => n.status === "ATIVA").reduce((s, n) => s + Number(n.valor_total), 0);

  return (
    <EstoqueLayout titulo="Entradas por NF" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro || erroContexto}</AvisoErro>
      {!contexto ? null : !contexto.abertura_concluida ? <AvisoSemAbertura C={C} /> : modo === "xml" ? (
        <ImportacaoXmlNF C={C} contexto={contexto} perm={perm} produtos={cad.produtos} unidades={cad.unidades} categorias={cad.categorias}
          onProdutoCriado={(p) => setCad((c) => ({ ...c, produtos: [...c.produtos, p] }))}
          onCancelar={() => setModo("lista")}
          onSalva={(nf) => {
            setModo("lista");
            carregarLista();
            getEstoqueFornecedores().then((fornecedores) => setCad((c) => ({ ...c, fornecedores }))).catch(() => {});
            setDetalhe({ id: nf.id });
          }} />
      ) : modo === "nova" ? (
        <FormNotaFiscal C={C} contexto={contexto} produtos={cad.produtos} unidades={cad.unidades}
          fornecedores={cad.fornecedores} setFornecedores={(fn) => setCad((c) => ({ ...c, fornecedores: fn(c.fornecedores) }))}
          onCancelar={() => setModo("lista")}
          onSalva={(nf, aviso) => { setModo("lista"); carregarLista(); setDetalhe({ id: nf.id, aviso }); }} />
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
            <div style={{ flex: "0 1 150px" }}><Campo label="De" C={C}><input type="date" value={filtros.inicio} onChange={(e) => setFiltros({ ...filtros, inicio: e.target.value })} style={inputStyle(C)} /></Campo></div>
            <div style={{ flex: "0 1 150px" }}><Campo label="Até" C={C}><input type="date" value={filtros.fim} onChange={(e) => setFiltros({ ...filtros, fim: e.target.value })} style={inputStyle(C)} /></Campo></div>
            <div style={{ flex: "1 1 180px" }}>
              <Campo label="Fornecedor" C={C}>
                <select value={filtros.fornecedor_id} onChange={(e) => setFiltros({ ...filtros, fornecedor_id: e.target.value })} style={inputStyle(C)}>
                  <option value="">Todos</option>
                  {cad.fornecedores.map((f) => <option key={f.id} value={f.id}>{f.razao_social}</option>)}
                </select>
              </Campo>
            </div>
            <div style={{ flex: "0 1 140px" }}>
              <Campo label="Status" C={C}>
                <select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })} style={inputStyle(C)}>
                  <option value="">Todas</option><option value="ATIVA">Ativas</option><option value="CANCELADA">Canceladas</option>
                </select>
              </Campo>
            </div>
            {perm.financeiro && (
              <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                <button onClick={() => setModo("nova")} style={btnStyle(C.brand, true)}><Plus size={15} /> Digitar NF</button>
                <button onClick={() => setModo("xml")} style={btnStyle(C.brand)}><FileCode2 size={15} /> Importar XML</button>
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{nfs.length} nota(s) · {formatBRL(totalLista)} em notas ativas</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {nfs.length === 0 && <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>Nenhuma nota fiscal lançada.</div>}
            {nfs.map((nf) => (
              <button key={nf.id} onClick={() => setDetalhe({ id: nf.id })}
                style={{ display: "flex", alignItems: "center", gap: 12, textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, cursor: "pointer", fontFamily: "inherit", color: C.text, opacity: nf.status === "CANCELADA" ? 0.55 : 1 }}>
                <FileText size={20} color={C.brand} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, textDecoration: nf.status === "CANCELADA" ? "line-through" : "none" }}>
                    NF {nf.numero}{nf.serie ? `/${nf.serie}` : ""} · {nf.fornecedor_nome}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, display: "flex", flexWrap: "wrap", gap: "2px 10px", marginTop: 2 }}>
                    <span>Entrada {formatData(nf.data_entrada)}</span>
                    <span>{nf.qtd_itens} item(ns)</span>
                    {Number(nf.divergencia_valor) !== 0 && <span style={{ color: C.warning }}>Diferença {formatBRL(nf.divergencia_valor)}</span>}
                    {nf.origem === "XML" ? <span><FileCode2 size={11} style={{ verticalAlign: "-1px" }} /> importada do XML</span>
                      : nf.tem_anexo && <span><Paperclip size={11} style={{ verticalAlign: "-1px" }} /> anexo</span>}
                    {nf.status === "CANCELADA" && <span style={{ color: C.danger }}>Cancelada</span>}
                  </div>
                </div>
                <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 800, whiteSpace: "nowrap" }}>{formatBRL(nf.valor_total)}</div>
              </button>
            ))}
          </div>
        </>
      )}
      {detalhe && <ModalDetalheNF C={C} nfId={detalhe.id} aviso={detalhe.aviso} perm={perm} onClose={() => setDetalhe(null)} onAlterada={carregarLista} />}
    </EstoqueLayout>
  );
}
